import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { createCrudHooks } from '../../shared/api/crud-hooks';
import {
  ajusteManualRcMovimento,
  cancelarRc,
  complementarRcMovimento,
  emitirNotaFiscalRc,
  encerrarRc,
  estornarRcMovimento,
  linkRcProjeto,
  listRcMovimentos,
  listRcProjetos,
  purchaseRequestsApi,
  unlinkRcProjeto,
} from './api';
import type {
  PurchaseRequestDto,
  PurchaseRequestInput,
  PurchaseRequestListItem,
  RcAjusteManualInput,
  RcComplementarInput,
  RcEmitirNfInput,
  RcEncerrarCancelarInput,
  RcMovimentoItem,
  RcProjetoVinculado,
} from './types';

export const purchaseRequestsHooks = createCrudHooks<
  PurchaseRequestListItem,
  PurchaseRequestDto,
  PurchaseRequestInput
>({
  queryKey: ['purchase-requests'],
  api: purchaseRequestsApi,
});

/** Extrato de movimentos da RC (card [89dB3aaI]). */
export function useRcMovimentos(
  purchaseRequestId: number | null,
): UseQueryResult<RcMovimentoItem[]> {
  return useQuery({
    queryKey: ['purchase-requests', 'movimentos', purchaseRequestId],
    queryFn: () => listRcMovimentos(purchaseRequestId as number),
    enabled: purchaseRequestId !== null && purchaseRequestId > 0,
    staleTime: 15_000,
  });
}

/** Estorna um lançamento e revalida o extrato + o detalhe da RC. */
export function useEstornarRcMovimento(
  purchaseRequestId: number,
): UseMutationResult<void, Error, { movimentoId: string; mensagem: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movimentoId, mensagem }) =>
      estornarRcMovimento(purchaseRequestId, movimentoId, { mensagem }),
    // Raiz cobre movimentos + detail + list de uma vez.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
}

/** Complementar (crédito) na RC — card [zn9Ds1NQ]. */
export function useComplementarRcMovimento(
  purchaseRequestId: number,
): UseMutationResult<void, Error, RcComplementarInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => complementarRcMovimento(purchaseRequestId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
}

/** Ajuste Manual (crédito/débito) na RC — card [zn9Ds1NQ]. */
export function useAjusteManualRcMovimento(
  purchaseRequestId: number,
): UseMutationResult<void, Error, RcAjusteManualInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => ajusteManualRcMovimento(purchaseRequestId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
}

/** Encerrar RC — card [vuaj4L1x]. */
export function useEncerrarRc(
  purchaseRequestId: number,
): UseMutationResult<void, Error, RcEncerrarCancelarInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => encerrarRc(purchaseRequestId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
}

/** Cancelar RC (movimento-based, substitui o DELETE) — card [vuaj4L1x]. */
export function useCancelarRc(
  purchaseRequestId: number,
): UseMutationResult<void, Error, RcEncerrarCancelarInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => cancelarRc(purchaseRequestId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
}

/** Emitir NF (Nota-Mãe) da RC — card [spjIqsFB]. */
export function useEmitirNotaFiscalRc(
  purchaseRequestId: number,
): UseMutationResult<void, Error, RcEmitirNfInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => emitirNotaFiscalRc(purchaseRequestId, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
}

/** Projetos vinculados à RC — card [uJoKy8Qk]. */
export function useRcProjetos(
  purchaseRequestId: number | null,
): UseQueryResult<RcProjetoVinculado[]> {
  return useQuery({
    queryKey: ['purchase-requests', 'projetos', purchaseRequestId],
    queryFn: () => listRcProjetos(purchaseRequestId as number),
    enabled: purchaseRequestId !== null && purchaseRequestId > 0,
    staleTime: 15_000,
  });
}

/** Vincular/desvincular projeto — invalida projetos + detalhe (contagem). */
export function useLinkRcProjeto(
  purchaseRequestId: number,
): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clienteProjetoCodigo) => linkRcProjeto(purchaseRequestId, clienteProjetoCodigo),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
}

export function useUnlinkRcProjeto(
  purchaseRequestId: number,
): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clienteProjetoCodigo) => unlinkRcProjeto(purchaseRequestId, clienteProjetoCodigo),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchase-requests'] }),
  });
}
